import React, { useState, useEffect } from 'react';

const Dashboard = ({ user, onLogout }) => {
  const [course, setCourse] = useState(null);
  const [books, setBooks] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [selectedTab, setSelectedTab] = useState('library');
  const [selectedBook, setSelectedBook] = useState(null);

  useEffect(() => {
    fetchCourse();
    fetchBooks();
    fetchPodcasts();
  }, []);

  const fetchCourse = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/course`);
      const courseData = await response.json();
      setCourse(courseData);
    } catch (error) {
      console.error('Error fetching course:', error);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/books`);
      const booksData = await response.json();
      setBooks(booksData);
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  const fetchPodcasts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/podcasts`);
      const podcastsData = await response.json();
      setPodcasts(podcastsData);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
    }
  };

  const getRoleInfo = () => {
    const roleNames = {
      'student': 'Aluno',
      'secretary': 'Secretária',
      'manager': 'Gestor',
      'preview': 'Visualização'
    };
    return {
      name: roleNames[user?.role] || 'Usuário',
      role: user?.role || 'student'
    };
  };

  const roleInfo = getRoleInfo();

  return (
    <div className="dashboard">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-content">
          <div className="logo" onClick={() => window.location.href = '/'} style={{ cursor: 'pointer' }}>
            Habeas Cripto
          </div>
          <div className="nav-actions">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ color: 'var(--silver-mist)' }}>
                {user.name} ({roleInfo.name})
              </span>
              <button className="btn-ghost" onClick={onLogout} data-testid="logout-btn">
                Sair
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div style={{ paddingTop: '120px', maxWidth: '1200px', margin: '0 auto', padding: '120px 2rem 4rem' }}>
        
        {/* Tab Navigation */}
        <div style={{ marginBottom: '3rem' }}>
          <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '1rem', flexWrap: 'wrap' }}>
            {[
              // Student tabs - limited access
              ...(user?.role === 'student' ? [
                { id: 'library', label: '📚 Biblioteca', access: ['student'] },
                { id: 'courses', label: '🎓 Cursos', access: ['student'] },
                { id: 'podcasts', label: '🎧 Podcasts', access: ['student'] },
                { id: 'news', label: '📰 Notícias', access: ['student'] },
                { id: 'mentorship', label: '👨‍🏫 Mentoria', access: ['student'] },
                { id: 'kyc', label: '🆔 KYC', access: ['student'] },
                { id: 'payment', label: '💳 Pagamento', access: ['student'] }
              ] : []),
              // Secretary tabs
              ...(user?.role === 'secretary' ? [
                { id: 'library', label: '📚 Biblioteca', access: ['secretary'] },
                { id: 'courses', label: '🎓 Cursos', access: ['secretary'] },
                { id: 'podcasts', label: '🎧 Podcasts', access: ['secretary'] },
                { id: 'mentorship', label: '👨‍🏫 Mentoria', access: ['secretary'] },
                { id: 'users', label: '👥 Usuários', access: ['secretary'] },
                { id: 'kyc', label: '🆔 KYC', access: ['secretary'] }
              ] : []),
              // Manager tabs - full access
              ...(user?.role === 'manager' ? [
                { id: 'library', label: '📚 Biblioteca', access: ['manager'] },
                { id: 'courses', label: '🎓 Cursos', access: ['manager'] },
                { id: 'podcasts', label: '🎧 Podcasts', access: ['manager'] },
                { id: 'mentorship', label: '👨‍🏫 Mentoria', access: ['manager'] },
                { id: 'news-admin', label: '📰 Notícias', access: ['manager'] },
                { id: 'users', label: '👥 Usuários', access: ['manager'] },
                { id: 'books-admin', label: '📖 Livros', access: ['manager'] },
                { id: 'content', label: '📝 Conteúdo', access: ['manager'] },
                { id: 'podcasts-admin', label: '🎙️ Podcasts', access: ['manager'] },
                { id: 'kyc-admin', label: '🆔 KYC Admin', access: ['manager'] },
                { id: 'crypto-admin', label: '💰 Crypto Config', access: ['manager'] },
                { id: 'financial', label: '💰 Financeiro', access: ['manager'] },
                { id: 'settings', label: '⚙️ Configurações', access: ['manager'] }
              ] : []),
              // Preview tabs - very limited
              ...(user?.role === 'preview' ? [
                { id: 'library', label: '📚 Biblioteca', access: ['preview'] },
                { id: 'courses', label: '🎓 Cursos', access: ['preview'] }
              ] : [])
            ].map((tab) => (
              <button
                key={tab.id}
                className={selectedTab === tab.id ? 'btn-primary' : 'btn-ghost'}
                onClick={() => setSelectedTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                style={{ padding: '0.75rem 1rem', fontSize: '0.9rem' }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="section-title" style={{ marginBottom: '3rem' }}>
          <h1 data-testid="dashboard-title">
            Plataforma Habeas Cripto
          </h1>
          <p style={{ opacity: 0.8, fontSize: '1.1rem' }}>
            Acesso aos livros, cursos e conteúdo especializado em direito digital
          </p>
        </div>

        {/* Library Tab */}
        {selectedTab === 'library' && (
          <LibrarySection books={books} onSelectBook={setSelectedBook} user={user} />
        )}

        {/* Courses Tab */}
        {selectedTab === 'courses' && (
          <CoursesSection course={course} user={user} />
        )}

        {/* Podcasts Tab */}
        {selectedTab === 'podcasts' && (
          <PodcastsSection podcasts={podcasts} user={user} />
        )}

        {/* News Tab for students */}
        {user?.role === 'student' && selectedTab === 'news' && (
          <NewsSection />
        )}

        {/* Mentorship Tab */}
        {selectedTab === 'mentorship' && (
          <MentorshipSection />
        )}

        {/* KYC Tab for students and secretary */}
        {(user?.role === 'student' || user?.role === 'secretary') && selectedTab === 'kyc' && (
          <KYCSection />
        )}

        {/* Payment Tab for students */}
        {user?.role === 'student' && selectedTab === 'payment' && (
          <PaymentSection />
        )}

        {/* Admin Tabs */}
        {(user?.role === 'manager' || user?.role === 'secretary') && selectedTab === 'users' && (
          <UserManagement userRole={user?.role} />
        )}

        {user?.role === 'manager' && selectedTab === 'news-admin' && (
          <NewsManagement />
        )}

        {user?.role === 'manager' && selectedTab === 'books-admin' && (
          <BooksManagement books={books} />
        )}

        {user?.role === 'manager' && selectedTab === 'content' && (
          <ContentManagement course={course} />
        )}

        {user?.role === 'manager' && selectedTab === 'podcasts-admin' && (
          <PodcastsManagement podcasts={podcasts} />
        )}

        {user?.role === 'manager' && selectedTab === 'financial' && (
          <FinancialDashboard />
        )}

        {user?.role === 'manager' && selectedTab === 'kyc-admin' && (
          <KYCManagement />
        )}

        {user?.role === 'manager' && selectedTab === 'crypto-admin' && (
          <CryptoManagement />
        )}

        {user?.role === 'manager' && selectedTab === 'settings' && (
          <SystemSettings />
        )}
      </div>

      {/* Book Reader Modal */}
      {selectedBook && (
        <BookReaderModal book={selectedBook} onClose={() => setSelectedBook(null)} />
      )}
    </div>
  );
};

// Library Section with book covers
const LibrarySection = ({ books, onSelectBook, user }) => {
  const getBookCover = (bookId) => {
    const covers = {
      'habeas-cripto': 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=300&h=400&fit=crop',
      'chave-mestra': 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=400&fit=crop',
      'estrategias-avancadas': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=400&fit=crop',
      'rota-cripto': 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=300&h=400&fit=crop',
      'trilhas-invisiveis': 'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=300&h=400&fit=crop',
      'valor-estavel': 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=300&h=400&fit=crop',
      'blockchain-forensics': 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=300&h=400&fit=crop',
      'defi-auto-custodia': 'https://images.unsplash.com/photo-1640340434855-6084b1f4901c?w=300&h=400&fit=crop',
      'evolucao-tributaria': 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=300&h=400&fit=crop',
      'metodo-anonimizacao': 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=300&h=400&fit=crop',
      'no-limite-da-lei': 'https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=300&h=400&fit=crop',
      'offshore-anonimato': 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=300&h=400&fit=crop'
    };
    return covers[bookId] || process.env.REACT_APP_DEFAULT_BOOK_COVER || 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=300&h=400&fit=crop';
  };

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>📚 Biblioteca Digital</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2rem' }}>
        {books.map((book) => (
          <div key={book.id} className="glass-card" style={{ padding: '2rem' }}>
            <div style={{ 
              height: '200px', 
              backgroundImage: `url(${getBookCover(book.id)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '8px', 
              marginBottom: '1rem',
              border: '1px solid rgba(255,255,255,0.1)'
            }}></div>
            <h4 style={{ color: 'var(--neon-green)', marginBottom: '0.5rem' }}>
              {book.title}
            </h4>
            <p style={{ color: 'var(--cyber-blue)', fontSize: '0.9rem', marginBottom: '1rem' }}>
              {book.subtitle}
            </p>
            <p style={{ fontSize: '0.85rem', marginBottom: '1.5rem', opacity: 0.8 }}>
              {book.description.substring(0, 100)}...
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button 
                className="btn-primary" 
                onClick={() => onSelectBook(book)}
                style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
              >
                📖 Ler Online
              </button>
              <a 
                href={book.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-ghost"
                style={{ fontSize: '0.85rem', padding: '0.5rem 1rem', textDecoration: 'none' }}
              >
                🛒 Comprar
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Courses Section
const CoursesSection = ({ course, user }) => {
  if (!course) return <div>Carregando cursos...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--neon-green)', margin: 0 }}>🎓 {course.title}</h2>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--cyber-orange)' }}>
            Em Breve
          </div>
          <p style={{ fontSize: '0.9rem', opacity: 0.7, margin: '0.5rem 0 0 0' }}>
            Lançamento previsto para 2025
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gap: '1.5rem' }}>
        {course.modules.map((module) => (
          <div key={module.id} className="glass-card" style={{ padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ color: 'var(--neon-green)', margin: 0, marginBottom: '0.5rem' }}>
                  Módulo {module.order}: {module.title}
                </h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--cyber-blue)', margin: 0, marginBottom: '1rem', opacity: 0.8 }}>
                  Baseado em: {module.based_on_book}
                </p>
                <p style={{ margin: 0, opacity: 0.8, marginBottom: '1rem' }}>{module.description}</p>
                
                {/* Show chapters */}
                {module.chapters && module.chapters.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h5 style={{ color: 'var(--cyber-blue)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      Conteúdo Programático:
                    </h5>
                    <ul style={{ fontSize: '0.85rem', opacity: 0.8, paddingLeft: '1.2rem', margin: 0 }}>
                      {module.chapters.map((chapter, idx) => (
                        <li key={idx} style={{ marginBottom: '0.25rem' }}>{chapter}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                <span style={{ 
                  background: 'rgba(255, 107, 53, 0.2)',
                  color: 'var(--cyber-orange)',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.875rem'
                }}>
                  🚀 Em Breve
                </span>
                <span style={{ color: 'var(--cyber-blue)', fontSize: '0.875rem' }}>
                  ⏱️ {module.duration_minutes} min
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Podcasts Section
const PodcastsSection = ({ podcasts, user }) => {
  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>🎧 Podcasts Habeas Cripto</h2>
      {podcasts.length === 0 ? (
        <div className="glass-card" style={{ padding: '3rem', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Em breve!</h3>
          <p style={{ opacity: 0.8 }}>
            Nossos podcasts sobre direito digital e criptoativos estarão disponíveis em breve.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '2rem' }}>
          {podcasts.map((podcast) => (
            <div key={podcast.id} className="glass-card" style={{ padding: '2rem' }}>
              <div style={{ display: 'flex', gap: '2rem' }}>
                <div style={{ 
                  width: '120px', 
                  height: '120px', 
                  background: 'var(--gradient-cyber)', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem'
                }}>
                  🎙️
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ color: 'var(--neon-green)', marginBottom: '0.5rem' }}>
                    {podcast.title}
                  </h4>
                  <p style={{ marginBottom: '1rem', opacity: 0.8 }}>
                    {podcast.description}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                    <button className="btn-primary" style={{ fontSize: '0.9rem' }}>
                      ▶ Reproduzir
                    </button>
                    <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                      ⏱️ {podcast.duration_minutes} minutos
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// News Section for students
const NewsSection = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/news`);
      const newsData = await response.json();
      setNews(newsData);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>📰 Notícias</h2>
        <div className="glass-card" style={{ padding: '3rem', textAlign: 'center' }}>
          <p>Carregando notícias...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>📰 Notícias</h2>
      {news.length === 0 ? (
        <div className="glass-card" style={{ padding: '3rem', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Em breve!</h3>
          <p style={{ opacity: 0.8 }}>
            As últimas notícias sobre direito digital e criptoativos estarão disponíveis em breve.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '2rem' }}>
          {news.map((article) => (
            <div key={article.id} className="glass-card" style={{ padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ color: 'var(--neon-green)', marginBottom: '0.5rem' }}>
                    {article.title}
                  </h4>
                  <p style={{ marginBottom: '1rem', opacity: 0.8, lineHeight: '1.6' }}>
                    {article.summary}
                  </p>
                  {article.content && (
                    <div style={{ 
                      fontSize: '0.9rem', 
                      opacity: 0.9, 
                      lineHeight: '1.6',
                      marginBottom: '1rem',
                      maxHeight: '200px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      {article.content.substring(0, 300)}
                      {article.content.length > 300 && '...'}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                  📅 {new Date(article.created_at).toLocaleDateString('pt-BR')}
                </span>
                <button className="btn-ghost" style={{ fontSize: '0.85rem' }}>
                  Ler mais
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Mentorship Section
const MentorshipSection = () => {
  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>👨‍🏫 Mentoria e Assessoria</h2>
      <div className="glass-card" style={{ padding: '3rem' }}>
        <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '2rem' }}>
          Mentoria Especializada em Estruturação P2P
        </h3>
        <p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.9, lineHeight: '1.6' }}>
          Receba assessoria completa para estruturação de empresas P2P no mercado de criptoativos, 
          incluindo compliance, aspectos regulatórios e estratégias de proteção patrimonial.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {[
            'Estrutura societária ideal',
            'Processos de compliance',
            'Integração bancária',
            'Gestão de riscos',
            'Orientação tributária',
            'Implementação KYC/AML'
          ].map((item, index) => (
            <div key={index} style={{ 
              background: 'rgba(0, 255, 136, 0.1)', 
              padding: '1rem', 
              borderRadius: '8px',
              borderLeft: '4px solid var(--neon-green)'
            }}>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>✓ {item}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <a 
            href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '5547991158926'}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ 
              padding: '1rem 2rem', 
              fontSize: '1.1rem',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📱 Solicitar Consultoria
          </a>
        </div>
      </div>
    </div>
  );
};

// News Management Component
const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [showAddNews, setShowAddNews] = useState(false);
  const [newsForm, setNewsForm] = useState({
    title: '', summary: '', content: ''
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/news`);
      const newsData = await response.json();
      setNews(newsData);
    } catch (error) {
      console.error('Error fetching news:', error);
    }
  };

  const handleAddNews = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/news`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newsForm)
      });
      setShowAddNews(false);
      setNewsForm({ title: '', summary: '', content: '' });
      fetchNews();
      alert('Notícia adicionada com sucesso!');
    } catch (error) {
      alert('Erro ao adicionar notícia');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--neon-green)', margin: 0 }}>📰 Gerenciamento de Notícias</h2>
        <button 
          className="btn-primary" 
          onClick={() => setShowAddNews(true)}
        >
          + Adicionar Notícia
        </button>
      </div>

      <div style={{ display: 'grid', gap: '2rem' }}>
        {news.map((article) => (
          <div key={article.id} className="glass-card" style={{ padding: '2rem' }}>
            <h4 style={{ color: 'var(--neon-green)', marginBottom: '0.5rem' }}>
              {article.title}
            </h4>
            <p style={{ marginBottom: '1rem', opacity: 0.8 }}>
              {article.summary}
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                {new Date(article.created_at).toLocaleDateString('pt-BR')}
              </span>
              <button className="btn-ghost" style={{ fontSize: '0.85rem' }}>
                Editar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add News Modal */}
      {showAddNews && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="glass-card" style={{ padding: '2rem', width: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Adicionar Notícia</h3>
            <form onSubmit={handleAddNews}>
              <div className="form-group">
                <label className="form-label">Título</label>
                <input
                  type="text"
                  className="form-input"
                  value={newsForm.title}
                  onChange={(e) => setNewsForm({...newsForm, title: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Resumo</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={newsForm.summary}
                  onChange={(e) => setNewsForm({...newsForm, summary: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Conteúdo Completo</label>
                <textarea
                  className="form-input"
                  rows="6"
                  value={newsForm.content}
                  onChange={(e) => setNewsForm({...newsForm, content: e.target.value})}
                  placeholder="Conteúdo da notícia será gerado por IA futuramente..."
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  Adicionar
                </button>
                <button 
                  type="button" 
                  className="btn-ghost" 
                  onClick={() => setShowAddNews(false)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// User Management Component (Enhanced with Edit Form)
const UserManagement = ({ userRole }) => {
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'student'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const usersData = await response.json();
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        fetchUsers();
        setShowAddUser(false);
        setNewUser({ name: '', email: '', password: '', role: 'student' });
        alert('Usuário criado com sucesso!');
      }
    } catch (error) {
      alert('Erro ao criar usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          fetchUsers();
          alert('Usuário excluído com sucesso!');
        }
      } catch (error) {
        alert('Erro ao excluir usuário');
      }
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--neon-green)', margin: 0 }}>👥 Gerenciamento de Usuários</h2>
        <button 
          className="btn-primary" 
          onClick={() => setShowAddUser(true)}
          data-testid="add-user-btn"
        >
          + Adicionar Usuário
        </button>
      </div>
      
      <div className="glass-card" style={{ padding: '2rem' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--cyber-blue)' }}>Nome</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--cyber-blue)' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--cyber-blue)' }}>Perfil</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--cyber-blue)' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--cyber-blue)' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <td style={{ padding: '1rem' }}>{user.name}</td>
                  <td style={{ padding: '1rem', opacity: 0.8 }}>{user.email}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      background: user.role === 'manager' ? 'rgba(0,255,136,0.2)' : 'rgba(0,255,255,0.2)',
                      color: user.role === 'manager' ? 'var(--neon-green)' : 'var(--cyber-blue)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem'
                    }}>
                      {user.role === 'manager' ? 'Gestor' : user.role === 'secretary' ? 'Secretária' : user.role === 'student' ? 'Aluno' : 'Preview'}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      background: user.subscription_status === 'enrolled' ? 'rgba(0,255,136,0.2)' : 'rgba(255,107,53,0.2)',
                      color: user.subscription_status === 'enrolled' ? 'var(--neon-green)' : 'var(--cyber-orange)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem'
                    }}>
                      {user.subscription_status === 'enrolled' ? 'Matriculado' : 'Livre'}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      className="btn-ghost" 
                      onClick={() => setEditingUser(user)}
                      style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', marginRight: '0.5rem' }}
                    >
                      Editar
                    </button>
                    <button 
                      onClick={() => handleDeleteUser(user.id)}
                      style={{
                        background: 'rgba(255, 107, 53, 0.2)',
                        color: 'var(--cyber-orange)',
                        border: '1px solid var(--cyber-orange)',
                        padding: '0.5rem 1rem',
                        fontSize: '0.85rem',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="glass-card" style={{ padding: '2rem', width: '400px' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Adicionar Usuário</h3>
            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label className="form-label">Nome</label>
                <input
                  type="text"
                  className="form-input"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Senha</label>
                <input
                  type="password"
                  className="form-input"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Perfil</label>
                <select
                  className="form-input"
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                >
                  <option value="student">Aluno</option>
                  <option value="secretary">Secretária</option>
                  <option value="manager">Gestor</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  Criar
                </button>
                <button 
                  type="button" 
                  className="btn-ghost" 
                  onClick={() => setShowAddUser(false)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="glass-card" style={{ padding: '2rem', width: '400px' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Editar Usuário</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const token = localStorage.getItem('token');
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${editingUser.id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    name: editingUser.name,
                    email: editingUser.email,
                    role: editingUser.role,
                    subscription_status: editingUser.subscription_status
                  })
                });
                
                setEditingUser(null);
                fetchUsers();
                alert('Usuário atualizado com sucesso!');
              } catch (error) {
                alert('Erro ao atualizar usuário');
              }
            }}>
              <div className="form-group">
                <label className="form-label">Nome</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Perfil</label>
                <select
                  className="form-input"
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                >
                  <option value="student">Aluno</option>
                  <option value="secretary">Secretária</option>
                  <option value="manager">Gestor</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status da Assinatura</label>
                <select
                  className="form-input"
                  value={editingUser.subscription_status || 'free'}
                  onChange={(e) => setEditingUser({...editingUser, subscription_status: e.target.value})}
                >
                  <option value="free">Livre</option>
                  <option value="enrolled">Matriculado</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  Salvar
                </button>
                <button 
                  type="button" 
                  className="btn-ghost" 
                  onClick={() => setEditingUser(null)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Books Management Component
const BooksManagement = ({ books }) => {
  const [editingBook, setEditingBook] = useState(null);
  const [bookForm, setBookForm] = useState({
    title: '', subtitle: '', price_print: 0, price_ebook: 0, 
    description: '', category: '', url: ''
  });

  const handleEditBook = (book) => {
    setEditingBook(book.id);
    setBookForm(book);
  };

  const handleSaveBook = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/book/${editingBook}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookForm)
      });
      setEditingBook(null);
      alert('Livro atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar livro');
    }
  };

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>📖 Gerenciamento de Livros</h2>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
        {books.map((book) => (
          <div key={book.id} className="glass-card" style={{ padding: '2rem' }}>
            {editingBook === book.id ? (
              <div>
                <div className="form-group">
                  <label className="form-label">Título</label>
                  <input
                    type="text"
                    className="form-input"
                    value={bookForm.title}
                    onChange={(e) => setBookForm({...bookForm, title: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Preço E-book</label>
                  <input
                    type="number"
                    className="form-input"
                    step="0.01"
                    value={bookForm.price_ebook}
                    onChange={(e) => setBookForm({...bookForm, price_ebook: parseFloat(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Descrição</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={bookForm.description}
                    onChange={(e) => setBookForm({...bookForm, description: e.target.value})}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn-primary" onClick={handleSaveBook} style={{ fontSize: '0.85rem' }}>
                    Salvar
                  </button>
                  <button className="btn-ghost" onClick={() => setEditingBook(null)} style={{ fontSize: '0.85rem' }}>
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h4 style={{ color: 'var(--neon-green)', marginBottom: '0.5rem' }}>
                  {book.title}
                </h4>
                <p style={{ color: 'var(--cyber-blue)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  {book.subtitle}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ color: 'var(--neon-green)' }}>
                    R$ {book.price_ebook.toFixed(2)}
                  </span>
                  <span style={{ opacity: 0.7 }}>
                    {book.category}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    className="btn-primary" 
                    onClick={() => handleEditBook(book)}
                    style={{ fontSize: '0.85rem' }}
                  >
                    Editar
                  </button>
                  <button className="btn-ghost" style={{ fontSize: '0.85rem' }}>
                    Upload
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Content Management Component (Enhanced)
const ContentManagement = ({ course }) => {
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    title: '', description: '', video_url: '', duration_minutes: 0, order: 0
  });

  const handleEditModule = (module) => {
    setEditingModule(module.id);
    setModuleForm(module);
  };

  const handleSaveModule = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/module/${editingModule}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(moduleForm)
      });
      setEditingModule(null);
      alert('Módulo atualizado com sucesso!');
      window.location.reload();
    } catch (error) {
      alert('Erro ao atualizar módulo');
    }
  };

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>📝 Gerenciamento de Conteúdo</h2>
      
      {course && course.modules.map((module) => (
        <div key={module.id} className="glass-card" style={{ padding: '2rem', marginBottom: '1rem' }}>
          {editingModule === module.id ? (
            <div>
              <div className="form-group">
                <label className="form-label">Título</label>
                <input
                  type="text"
                  className="form-input"
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({...moduleForm, title: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({...moduleForm, description: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">URL do Vídeo (Vimeo)</label>
                <input
                  type="url"
                  className="form-input"
                  value={moduleForm.video_url}
                  onChange={(e) => setModuleForm({...moduleForm, video_url: e.target.value})}
                  placeholder="https://vimeo.com/123456789"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Duração (minutos)</label>
                <input
                  type="number"
                  className="form-input"
                  value={moduleForm.duration_minutes}
                  onChange={(e) => setModuleForm({...moduleForm, duration_minutes: parseInt(e.target.value)})}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn-primary" onClick={handleSaveModule}>
                  Salvar
                </button>
                <button className="btn-ghost" onClick={() => setEditingModule(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ color: 'var(--neon-green)', margin: 0 }}>
                    Módulo {module.order}: {module.title}
                  </h4>
                  <p style={{ margin: '0.5rem 0', opacity: 0.8, fontSize: '0.9rem' }}>
                    {module.description}
                  </p>
                  <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                    ⏱️ {module.duration_minutes} min
                    {module.video_url && <span> | 🎥 Vídeo configurado</span>}
                  </div>
                </div>
                <button 
                  className="btn-primary" 
                  onClick={() => handleEditModule(module)}
                  style={{ fontSize: '0.85rem' }}
                >
                  Editar
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Podcasts Management Component
const PodcastsManagement = ({ podcasts }) => {
  const [showAddPodcast, setShowAddPodcast] = useState(false);
  const [podcastForm, setPodcastForm] = useState({
    title: '', description: '', audio_url: '', duration_minutes: 0
  });

  const handleAddPodcast = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/podcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(podcastForm)
      });
      setShowAddPodcast(false);
      setPodcastForm({ title: '', description: '', audio_url: '', duration_minutes: 0 });
      alert('Podcast adicionado com sucesso!');
    } catch (error) {
      alert('Erro ao adicionar podcast');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--neon-green)', margin: 0 }}>🎙️ Gerenciamento de Podcasts</h2>
        <button 
          className="btn-primary" 
          onClick={() => setShowAddPodcast(true)}
        >
          + Adicionar Podcast
        </button>
      </div>

      <div style={{ display: 'grid', gap: '2rem' }}>
        {podcasts.map((podcast) => (
          <div key={podcast.id} className="glass-card" style={{ padding: '2rem' }}>
            <h4 style={{ color: 'var(--neon-green)', marginBottom: '0.5rem' }}>
              {podcast.title}
            </h4>
            <p style={{ marginBottom: '1rem', opacity: 0.8 }}>
              {podcast.description}
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                ⏱️ {podcast.duration_minutes} minutos
              </span>
              <button className="btn-ghost" style={{ fontSize: '0.85rem' }}>
                Editar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Podcast Modal */}
      {showAddPodcast && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="glass-card" style={{ padding: '2rem', width: '500px' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Adicionar Podcast</h3>
            <form onSubmit={handleAddPodcast}>
              <div className="form-group">
                <label className="form-label">Título</label>
                <input
                  type="text"
                  className="form-input"
                  value={podcastForm.title}
                  onChange={(e) => setPodcastForm({...podcastForm, title: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={podcastForm.description}
                  onChange={(e) => setPodcastForm({...podcastForm, description: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">URL do Áudio</label>
                <input
                  type="url"
                  className="form-input"
                  value={podcastForm.audio_url}
                  onChange={(e) => setPodcastForm({...podcastForm, audio_url: e.target.value})}
                  placeholder="https://exemplo.com/audio.mp3"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Duração (minutos)</label>
                <input
                  type="number"
                  className="form-input"
                  value={podcastForm.duration_minutes}
                  onChange={(e) => setPodcastForm({...podcastForm, duration_minutes: parseInt(e.target.value)})}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  Adicionar
                </button>
                <button 
                  type="button" 
                  className="btn-ghost" 
                  onClick={() => setShowAddPodcast(false)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Financial Dashboard Component (Enhanced)
const FinancialDashboard = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [importType, setImportType] = useState('financial');

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('data_type', importType);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        alert(`CSV de ${importType} importado com sucesso!`);
        setCsvFile(null);
      } else {
        throw new Error('Erro na importação');
      }
    } catch (error) {
      alert('Erro ao importar CSV');
    }
  };

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>💰 Dashboard Financeiro</h2>
      
      {/* CSV Upload */}
      <div className="glass-card" style={{ padding: '2rem', marginBottom: '2rem' }}>
        <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Importar Dados CSV</h3>
        
        <div className="form-group">
          <label className="form-label">Tipo de Dados</label>
          <select
            className="form-input"
            value={importType}
            onChange={(e) => setImportType(e.target.value)}
            style={{ cursor: 'pointer', marginBottom: '1rem' }}
          >
            <option value="financial">Dados Financeiros</option>
            <option value="transactions">Transações</option>
            <option value="users">Usuários</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setCsvFile(e.target.files[0])}
            style={{ 
              background: 'rgba(255,255,255,0.1)', 
              color: 'var(--silver-mist)',
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid rgba(255,255,255,0.2)',
              flex: 1
            }}
          />
          <button 
            className="btn-primary" 
            onClick={handleCsvUpload}
            disabled={!csvFile}
          >
            📄 Importar CSV
          </button>
        </div>
        
        <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.5rem' }}>
          <p>Formatos suportados: Dados financeiros, transações, usuários</p>
        </div>
      </div>

      {/* Financial Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
        <div className="glass-card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Receita Total</h3>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--neon-green)' }}>
            R$ 25.640,00
          </div>
          <div style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '0.5rem' }}>
            +18% vs mês anterior
          </div>
        </div>

        <div className="glass-card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Alunos Ativos</h3>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--cyber-blue)' }}>
            142
          </div>
          <div style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '0.5rem' }}>
            +12 novos esta semana
          </div>
        </div>

        <div className="glass-card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Taxa de Conversão</h3>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--cyber-orange)' }}>
            31.2%
          </div>
          <div style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '0.5rem' }}>
            Visitantes → Matrículas
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="glass-card" style={{ padding: '2rem' }}>
        <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Transações Recentes</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Data</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Cliente</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Produto</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Valor</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {[
                { date: '28/09/2025', client: 'João Silva', product: 'Livro - Habeas Cripto', value: 'R$ 142,36', status: 'Pago' },
                { date: '27/09/2025', client: 'Maria Santos', product: 'Mentoria P2P', value: 'R$ 5.000,00', status: 'Pago' },
                { date: '26/09/2025', client: 'Pedro Costa', product: 'Livro - Chave Mestra', value: 'R$ 177,98', status: 'Pendente' },
                { date: '25/09/2025', client: 'Ana Oliveira', product: 'Livro Digital Bundle', value: 'R$ 650,00', status: 'Pago' }
              ].map((transaction, index) => (
                <tr key={index} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <td style={{ padding: '1rem' }}>{transaction.date}</td>
                  <td style={{ padding: '1rem' }}>{transaction.client}</td>
                  <td style={{ padding: '1rem' }}>{transaction.product}</td>
                  <td style={{ padding: '1rem', color: 'var(--neon-green)' }}>{transaction.value}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      background: transaction.status === 'Pago' ? 'rgba(0,255,136,0.2)' : 'rgba(255,107,53,0.2)',
                      color: transaction.status === 'Pago' ? 'var(--neon-green)' : 'var(--cyber-orange)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem'
                    }}>
                      {transaction.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Web3 Authentication Configuration Component
const Web3AuthenticationConfig = () => {
  const [web3Config, setWeb3Config] = useState({
    enableWalletLogin: false,
    supportedWallets: ['metamask', 'walletconnect'],
    networkId: '1', // Ethereum mainnet
    rpcUrl: '',
    blockExplorer: ''
  });
  const [walletConnected, setWalletConnected] = useState(false);
  const [currentAccount, setCurrentAccount] = useState('');

  // Check if MetaMask is installed
  const isMetaMaskInstalled = () => {
    return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';
  };

  // Connect to MetaMask
  const connectWallet = async () => {
    if (!isMetaMaskInstalled()) {
      alert('MetaMask não está instalado. Por favor, instale para continuar.');
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      if (accounts.length > 0) {
        setCurrentAccount(accounts[0]);
        setWalletConnected(true);
        alert('Carteira conectada com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao conectar carteira:', error);
      alert('Erro ao conectar carteira');
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setCurrentAccount('');
    setWalletConnected(false);
  };

  // Get network name
  const getNetworkName = (networkId) => {
    const networks = {
      '1': 'Ethereum Mainnet',
      '137': 'Polygon Mainnet',
      '56': 'BSC Mainnet',
      '5': 'Ethereum Goerli Testnet'
    };
    return networks[networkId] || 'Rede Personalizada';
  };

  const handleSaveWeb3Config = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/web3-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(web3Config)
      });

      if (response.ok) {
        alert('Configurações Web3 salvas com sucesso!');
      }
    } catch (error) {
      alert('Erro ao salvar configurações Web3');
    }
  };

  return (
    <div className="glass-card" style={{ padding: '2rem' }}>
      <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '2rem' }}>🌐 Configuração de Autenticação Web3</h3>
      
      {/* Wallet Connection Status */}
      <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '2rem', background: 'rgba(0,255,136,0.1)' }}>
        <h4 style={{ color: 'var(--neon-green)', marginBottom: '1rem' }}>Status da Carteira</h4>
        
        {!isMetaMaskInstalled() ? (
          <div>
            <p style={{ color: 'var(--cyber-orange)', marginBottom: '1rem' }}>
              ⚠️ MetaMask não detectado
            </p>
            <a 
              href="https://metamask.io/download/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ textDecoration: 'none' }}
            >
              🦊 Instalar MetaMask
            </a>
          </div>
        ) : walletConnected ? (
          <div>
            <p style={{ color: 'var(--neon-green)', marginBottom: '1rem' }}>
              ✅ Carteira Conectada
            </p>
            <div style={{ 
              background: 'rgba(255,255,255,0.1)', 
              padding: '0.75rem', 
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              marginBottom: '1rem',
              wordBreak: 'break-all'
            }}>
              {currentAccount}
            </div>
            <button 
              className="btn-ghost" 
              onClick={disconnectWallet}
              style={{ fontSize: '0.9rem' }}
            >
              Desconectar
            </button>
          </div>
        ) : (
          <div>
            <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
              Conecte sua carteira para testar a integração
            </p>
            <button 
              className="btn-primary" 
              onClick={connectWallet}
            >
              🔗 Conectar MetaMask
            </button>
          </div>
        )}
      </div>

      {/* Web3 Configuration */}
      <div className="form-group">
        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={web3Config.enableWalletLogin}
            onChange={(e) => setWeb3Config({...web3Config, enableWalletLogin: e.target.checked})}
          />
          Habilitar Login com Carteira
        </label>
        <small style={{ opacity: 0.7, fontSize: '0.85rem' }}>
          Permite que usuários façam login usando MetaMask e outras carteiras
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Rede Blockchain</label>
        <select
          className="form-input"
          value={web3Config.networkId}
          onChange={(e) => setWeb3Config({...web3Config, networkId: e.target.value})}
          disabled={!web3Config.enableWalletLogin}
        >
          <option value="1">Ethereum Mainnet</option>
          <option value="137">Polygon Mainnet</option>
          <option value="56">BSC Mainnet</option>
          <option value="5">Ethereum Goerli (Testnet)</option>
        </select>
        <small style={{ opacity: 0.7, fontSize: '0.85rem' }}>
          Rede selecionada: {getNetworkName(web3Config.networkId)}
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Carteiras Suportadas</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {[
            { id: 'metamask', label: '🦊 MetaMask' },
            { id: 'walletconnect', label: '📱 WalletConnect' },
            { id: 'coinbase', label: '🔵 Coinbase Wallet' }
          ].map((wallet) => (
            <label key={wallet.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={web3Config.supportedWallets.includes(wallet.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setWeb3Config({
                      ...web3Config, 
                      supportedWallets: [...web3Config.supportedWallets, wallet.id]
                    });
                  } else {
                    setWeb3Config({
                      ...web3Config, 
                      supportedWallets: web3Config.supportedWallets.filter(w => w !== wallet.id)
                    });
                  }
                }}
                disabled={!web3Config.enableWalletLogin}
              />
              {wallet.label}
            </label>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">RPC URL (Opcional)</label>
        <input
          type="url"
          className="form-input"
          placeholder="https://rpc.ankr.com/eth"
          value={web3Config.rpcUrl}
          onChange={(e) => setWeb3Config({...web3Config, rpcUrl: e.target.value})}
          disabled={!web3Config.enableWalletLogin}
        />
        <small style={{ opacity: 0.7, fontSize: '0.85rem' }}>
          URL customizada do provedor RPC (deixe vazio para usar padrão)
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Block Explorer</label>
        <input
          type="url"
          className="form-input"
          placeholder="https://etherscan.io"
          value={web3Config.blockExplorer}
          onChange={(e) => setWeb3Config({...web3Config, blockExplorer: e.target.value})}
          disabled={!web3Config.enableWalletLogin}
        />
      </div>

      {/* Preview */}
      {web3Config.enableWalletLogin && (
        <div className="glass-card" style={{ padding: '1.5rem', marginTop: '2rem', background: 'rgba(0,255,255,0.1)' }}>
          <h4 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>👁️ Preview Login</h4>
          <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
            Como aparecerá para os usuários na tela de login:
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button className="btn-primary" style={{ fontSize: '0.9rem' }}>
              📧 Login com Email
            </button>
            <button className="btn-secondary" style={{ fontSize: '0.9rem' }}>
              🦊 Conectar Carteira
            </button>
          </div>
        </div>
      )}

      <button 
        className="btn-primary" 
        onClick={handleSaveWeb3Config}
        style={{ width: '100%', marginTop: '2rem' }}
      >
        💾 Salvar Configurações Web3
      </button>
    </div>
  );
};

// System Settings Component (Enhanced with LLM Configuration)
const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('llm');
  const [videoSettings, setVideoSettings] = useState({
    vimeoUrl: '',
    moduleId: '1'
  });
  const [llmSettings, setLlmSettings] = useState({
    provider: 'openai',
    api_key: '',
    model_name: ''
  });
  const [videoFile, setVideoFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [bookFile, setBookFile] = useState(null);

  const handleVideoUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/module/${videoSettings.moduleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          video_url: videoSettings.vimeoUrl
        })
      });
      alert('Vídeo atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar vídeo');
    }
  };

  const handleLLMSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/llm-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(llmSettings)
      });
      alert('Configurações LLM salvas com sucesso!');
    } catch (error) {
      alert('Erro ao salvar configurações LLM');
    }
  };

  const handleFileUpload = async (type, file, additionalData = {}) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add additional form data
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload/${type}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        alert(`${type.charAt(0).toUpperCase() + type.slice(1)} enviado com sucesso!`);
        // Reset file input
        if (type === 'video') setVideoFile(null);
        if (type === 'audio') setAudioFile(null);
        if (type === 'book') setBookFile(null);
      }
    } catch (error) {
      alert(`Erro ao enviar ${type}`);
    }
  };

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>⚙️ Configurações do Sistema</h2>
      
      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '1rem' }}>
          {[
            { id: 'llm', label: '🤖 Configuração LLM' },
            { id: 'web3', label: '🌐 Web3 Auth' },
            { id: 'videos', label: '🎥 Vídeos' },
            { id: 'audio', label: '🎧 Áudios' },
            { id: 'books', label: '📚 Livros' },
            { id: 'thumbnails', label: '🖼️ Miniaturas' }
          ].map((tab) => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? 'btn-primary' : 'btn-ghost'}
              onClick={() => setActiveTab(tab.id)}
              style={{ padding: '0.75rem 1rem', fontSize: '0.9rem' }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Web3 Authentication Tab */}
        {activeTab === 'web3' && (
          <Web3AuthenticationConfig />
        )}

        {/* LLM Configuration Tab */}
        {activeTab === 'llm' && (
          <div className="glass-card" style={{ padding: '2rem' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>🤖 Configuração de LLM</h3>
            <p style={{ opacity: 0.8, marginBottom: '2rem' }}>
              Configure o provedor de IA para geração automática de conteúdo de notícias
            </p>
            
            <div className="form-group">
              <label className="form-label">Provedor LLM *</label>
              <select
                className="form-input"
                value={llmSettings.provider}
                onChange={(e) => setLlmSettings({...llmSettings, provider: e.target.value})}
                style={{ cursor: 'pointer' }}
              >
                <option value="openai">OpenAI (GPT-4, GPT-5)</option>
                <option value="gemini">Google Gemini</option>
                <option value="claude">Anthropic Claude</option>
                <option value="grok">xAI Grok</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">API Key *</label>
              <input
                type="password"
                className="form-input"
                placeholder="Cole sua API key aqui..."
                value={llmSettings.api_key}
                onChange={(e) => setLlmSettings({...llmSettings, api_key: e.target.value})}
              />
              <small style={{ opacity: 0.7, fontSize: '0.85rem' }}>
                Sua API key será armazenada de forma segura
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Modelo (Opcional)</label>
              <input
                type="text"
                className="form-input"
                placeholder="Ex: gpt-4, gpt-5, gemini-pro, claude-3-sonnet"
                value={llmSettings.model_name}
                onChange={(e) => setLlmSettings({...llmSettings, model_name: e.target.value})}
              />
              <small style={{ opacity: 0.7, fontSize: '0.85rem' }}>
                Deixe em branco para usar o modelo padrão do provedor
              </small>
            </div>

            <button 
              className="btn-primary" 
              onClick={handleLLMSave}
              disabled={!llmSettings.provider || !llmSettings.api_key}
              style={{ width: '100%' }}
            >
              💾 Salvar Configuração LLM
            </button>
          </div>
        )}

        {/* Video Management Tab */}
        {activeTab === 'videos' && (
          <div className="glass-card" style={{ padding: '2rem' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>🎥 Gerenciamento de Vídeos</h3>
            
            {/* Vimeo URL Configuration */}
            <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <h4 style={{ color: 'var(--neon-green)', marginBottom: '1rem' }}>Configurar URL do Vimeo</h4>
              <div className="form-group">
                <label className="form-label">Módulo</label>
                <select
                  className="form-input"
                  value={videoSettings.moduleId}
                  onChange={(e) => setVideoSettings({...videoSettings, moduleId: e.target.value})}
                >
                  {Array.from({length: 10}, (_, i) => (
                    <option key={i+1} value={i+1}>Módulo {i+1}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">URL do Vimeo</label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="https://vimeo.com/123456789"
                  value={videoSettings.vimeoUrl}
                  onChange={(e) => setVideoSettings({...videoSettings, vimeoUrl: e.target.value})}
                />
              </div>
              <button 
                className="btn-primary" 
                onClick={handleVideoUpdate}
                data-testid="update-video-btn"
              >
                Atualizar Vídeo
              </button>
            </div>

            {/* Video Upload */}
            <div>
              <h4 style={{ color: 'var(--neon-green)', marginBottom: '1rem' }}>Upload de Vídeo</h4>
              <div className="form-group">
                <label className="form-label">Arquivo de Vídeo</label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files[0])}
                  style={{ 
                    background: 'rgba(255,255,255,0.1)', 
                    color: 'var(--silver-mist)',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    width: '100%'
                  }}
                />
              </div>
              <button 
                className="btn-primary" 
                onClick={() => handleFileUpload('video', videoFile, { title: 'Novo Vídeo', description: 'Upload via painel' })}
                disabled={!videoFile}
              >
                📤 Fazer Upload de Vídeo
              </button>
            </div>
          </div>
        )}

        {/* Audio Management Tab */}
        {activeTab === 'audio' && (
          <div className="glass-card" style={{ padding: '2rem' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>🎧 Gerenciamento de Áudios</h3>
            <div className="form-group">
              <label className="form-label">Arquivo de Áudio</label>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setAudioFile(e.target.files[0])}
                style={{ 
                  background: 'rgba(255,255,255,0.1)', 
                  color: 'var(--silver-mist)',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  width: '100%'
                }}
              />
            </div>
            <button 
              className="btn-primary" 
              onClick={() => handleFileUpload('audio', audioFile, { title: 'Novo Podcast', description: 'Upload via painel', duration_minutes: 30 })}
              disabled={!audioFile}
              data-testid="upload-audio-btn"
            >
              📤 Fazer Upload de Áudio
            </button>
          </div>
        )}

        {/* Books Management Tab */}
        {activeTab === 'books' && (
          <div className="glass-card" style={{ padding: '2rem' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>📚 Gerenciamento de Livros</h3>
            <div className="form-group">
              <label className="form-label">Arquivo do Livro (PDF/EPUB)</label>
              <input
                type="file"
                accept=".pdf,.epub"
                onChange={(e) => setBookFile(e.target.files[0])}
                style={{ 
                  background: 'rgba(255,255,255,0.1)', 
                  color: 'var(--silver-mist)',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  width: '100%'
                }}
              />
            </div>
            <button 
              className="btn-primary" 
              onClick={() => handleFileUpload('book', bookFile, { 
                title: 'Novo Livro', 
                subtitle: 'Upload via painel', 
                price_print: 50.00,
                price_ebook: 25.00,
                description: 'Livro enviado via painel administrativo',
                category: 'Criptoativos'
              })}
              disabled={!bookFile}
              data-testid="upload-book-btn"
            >
              📤 Fazer Upload de Livro
            </button>
          </div>
        )}

        {/* Thumbnail Management Tab */}
        {activeTab === 'thumbnails' && (
          <ThumbnailManagement />
        )}
      </div>
    </div>
  );
};

// KYC Section for Students
const KYCSection = () => {
  const [kycDocuments, setKycDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKycDocuments();
  }, []);

  const fetchKycDocuments = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kyc-documents`);
      const documents = await response.json();
      setKycDocuments(documents);
    } catch (error) {
      console.error('Error fetching KYC documents:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Carregando documentos KYC...</div>;
  }

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>🆔 KYC - Know Your Customer</h2>
      
      <div className="glass-card" style={{ padding: '2rem', marginBottom: '2rem' }}>
        <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>O que é KYC?</h3>
        <p style={{ opacity: 0.9, lineHeight: '1.6', marginBottom: '1rem' }}>
          KYC (Know Your Customer) é um processo de verificação de identidade obrigatório para 
          acessar determinados serviços financeiros e educacionais. Este processo garante segurança 
          e conformidade com regulamentações.
        </p>
        <p style={{ opacity: 0.9, lineHeight: '1.6' }}>
          Para completar seu KYC, você precisará fornecer documentos válidos de identificação 
          e comprovação de endereço.
        </p>
      </div>

      {kycDocuments.length === 0 ? (
        <div className="glass-card" style={{ padding: '3rem', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Documentos em Preparação</h3>
          <p style={{ opacity: 0.8 }}>
            Os exemplos e requisitos de KYC estarão disponíveis em breve.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '2rem' }}>
          {kycDocuments.map((doc) => (
            <div key={doc.id} className="glass-card" style={{ padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ color: 'var(--neon-green)', margin: 0, marginBottom: '0.5rem' }}>
                    {doc.title}
                    {doc.is_required && (
                      <span style={{ 
                        color: 'var(--cyber-orange)', 
                        fontSize: '0.75rem', 
                        marginLeft: '0.5rem',
                        background: 'rgba(255, 107, 53, 0.2)',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px'
                      }}>
                        OBRIGATÓRIO
                      </span>
                    )}
                  </h4>
                  <p style={{ margin: 0, opacity: 0.8, marginBottom: '1rem' }}>{doc.description}</p>
                </div>
                <span style={{ 
                  background: doc.document_type === 'requirement' ? 'rgba(255, 107, 53, 0.2)' : 'rgba(0, 255, 136, 0.2)',
                  color: doc.document_type === 'requirement' ? 'var(--cyber-orange)' : 'var(--neon-green)',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap'
                }}>
                  {doc.document_type === 'requirement' ? '📋 Requisito' : 
                   doc.document_type === 'template' ? '📄 Modelo' : '💡 Exemplo'}
                </span>
              </div>
              
              {doc.file_url && (
                <div style={{ marginTop: '1rem' }}>
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-primary"
                    style={{ 
                      fontSize: '0.9rem', 
                      padding: '0.75rem 1.5rem',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    📄 Ver Documento
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Payment Section for Students
const PaymentSection = () => {
  const [cryptoSettings, setCryptoSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCryptoSettings();
  }, []);

  const fetchCryptoSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crypto-settings`);
      const settings = await response.json();
      setCryptoSettings(settings);
    } catch (error) {
      console.error('Error fetching crypto settings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Carregando informações de pagamento...</div>;
  }

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>💳 Opções de Pagamento</h2>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
        
        {/* PIX Payment */}
        <div className="glass-card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            🇧🇷 Pagamento PIX
          </h3>
          
          {cryptoSettings?.qr_code_pix ? (
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <img 
                src={cryptoSettings.qr_code_pix.startsWith('http') ? cryptoSettings.qr_code_pix : `${process.env.REACT_APP_BACKEND_URL}${cryptoSettings.qr_code_pix}`} 
                alt="QR Code PIX" 
                style={{ 
                  width: '200px', 
                  height: '200px', 
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_76383d17-7048-44a3-9a92-60f17acf3582/artifacts/akkkhcyz_WhatsApp%20Image%202025-09-28%20at%2010.17.48.jpeg" 
                alt="QR Code PIX" 
                style={{ 
                  width: '200px', 
                  height: '200px', 
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  objectFit: 'cover'
                }}
              />
            </div>
          )}
          
          {cryptoSettings?.pix_key && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.9rem', opacity: 0.8, marginBottom: '0.5rem' }}>
                Chave PIX:
              </label>
              <div style={{ 
                background: 'rgba(255,255,255,0.1)', 
                padding: '0.75rem', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                wordBreak: 'break-all'
              }}>
                {cryptoSettings.pix_key}
              </div>
            </div>
          )}
          
          {cryptoSettings?.pix_recipient_name && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.9rem', opacity: 0.8, marginBottom: '0.5rem' }}>
                Beneficiário:
              </label>
              <div style={{ 
                background: 'rgba(255,255,255,0.1)', 
                padding: '0.75rem', 
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}>
                {cryptoSettings.pix_recipient_name}
              </div>
            </div>
          )}
          
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <button className="btn-primary" style={{ width: '100%' }}>
              📱 Abrir App do Banco
            </button>
          </div>
        </div>

        {/* Crypto Payment */}
        <div className="glass-card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            ₿ Pagamento Crypto
          </h3>
          
          {cryptoSettings?.wallet_address ? (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', opacity: 0.8, marginBottom: '0.5rem' }}>
                  Tipo de Carteira:
                </label>
                <div style={{ 
                  background: 'rgba(0, 255, 136, 0.2)', 
                  color: 'var(--neon-green)',
                  padding: '0.5rem 1rem', 
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  display: 'inline-block',
                  textTransform: 'uppercase'
                }}>
                  {cryptoSettings.wallet_type}
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', opacity: 0.8, marginBottom: '0.5rem' }}>
                  Rede:
                </label>
                <div style={{ 
                  background: 'rgba(0, 255, 255, 0.2)', 
                  color: 'var(--cyber-blue)',
                  padding: '0.5rem 1rem', 
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  display: 'inline-block',
                  textTransform: 'uppercase'
                }}>
                  {cryptoSettings.network}
                </div>
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', opacity: 0.8, marginBottom: '0.5rem' }}>
                  Endereço da Carteira:
                </label>
                <div style={{ 
                  background: 'rgba(255,255,255,0.1)', 
                  padding: '0.75rem', 
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all',
                  border: '1px solid rgba(0, 255, 136, 0.3)'
                }}>
                  {cryptoSettings.wallet_address}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  className="btn-secondary" 
                  onClick={() => navigator.clipboard.writeText(cryptoSettings.wallet_address)}
                  style={{ flex: 1 }}
                >
                  📋 Copiar
                </button>
                <button className="btn-primary" style={{ flex: 1 }}>
                  🚀 Abrir Wallet
                </button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ opacity: 0.8 }}>
                Configurações de pagamento crypto em preparação.
              </p>
            </div>
          )}
        </div>
      </div>
      
      <div className="glass-card" style={{ padding: '2rem', marginTop: '2rem' }}>
        <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>📞 Suporte</h3>
        <p style={{ opacity: 0.9, lineHeight: '1.6' }}>
          Precisa de ajuda com o pagamento? Entre em contato conosco:
        </p>
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <a 
            href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '5547991158926'}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ textDecoration: 'none' }}
          >
            📱 WhatsApp
          </a>
          <button className="btn-ghost">
            📧 Email
          </button>
        </div>
      </div>
    </div>
  );
};

// KYC Management for Manager
const KYCManagement = () => {
  const [kycDocuments, setKycDocuments] = useState([]);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    description: '',
    document_type: 'example',
    is_required: false,
    order: 0
  });
  const [documentFile, setDocumentFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/kyc-documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const documents = await response.json();
      setKycDocuments(documents);
    } catch (error) {
      console.error('Error fetching KYC documents:', error);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!documentFile) {
      alert('Selecione um arquivo');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('title', documentForm.title);
      formData.append('description', documentForm.description);
      formData.append('document_type', documentForm.document_type);
      formData.append('is_required', documentForm.is_required);
      formData.append('order', documentForm.order);

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload/kyc-document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        alert('Documento KYC enviado com sucesso!');
        setShowAddDocument(false);
        setDocumentForm({ title: '', description: '', document_type: 'example', is_required: false, order: 0 });
        setDocumentFile(null);
        fetchDocuments();
      }
    } catch (error) {
      alert('Erro ao enviar documento KYC');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Tem certeza que deseja excluir este documento?')) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/kyc-documents/${documentId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        fetchDocuments();
        alert('Documento excluído com sucesso!');
      } catch (error) {
        alert('Erro ao excluir documento');
      }
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--neon-green)', margin: 0 }}>🆔 Gerenciamento KYC</h2>
        <button 
          className="btn-primary" 
          onClick={() => setShowAddDocument(true)}
        >
          + Adicionar Documento
        </button>
      </div>

      <div style={{ display: 'grid', gap: '2rem' }}>
        {kycDocuments.map((doc) => (
          <div key={doc.id} className="glass-card" style={{ padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ color: 'var(--neon-green)', marginBottom: '0.5rem' }}>
                  {doc.title}
                  {doc.is_required && (
                    <span style={{ 
                      color: 'var(--cyber-orange)', 
                      fontSize: '0.75rem', 
                      marginLeft: '0.5rem',
                      background: 'rgba(255, 107, 53, 0.2)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px'
                    }}>
                      OBRIGATÓRIO
                    </span>
                  )}
                </h4>
                <p style={{ marginBottom: '1rem', opacity: 0.8 }}>{doc.description}</p>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <span style={{ 
                    background: doc.document_type === 'requirement' ? 'rgba(255, 107, 53, 0.2)' : 'rgba(0, 255, 136, 0.2)',
                    color: doc.document_type === 'requirement' ? 'var(--cyber-orange)' : 'var(--neon-green)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.8rem'
                  }}>
                    {doc.document_type}
                  </span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                    Ordem: {doc.order}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {doc.file_url && (
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-ghost"
                    style={{ fontSize: '0.85rem', textDecoration: 'none' }}
                  >
                    📄 Ver
                  </a>
                )}
                <button 
                  className="btn-ghost" 
                  onClick={() => setEditingDocument(doc)}
                  style={{ fontSize: '0.85rem' }}
                >
                  Editar
                </button>
                <button 
                  onClick={() => handleDeleteDocument(doc.id)}
                  style={{
                    background: 'rgba(255, 107, 53, 0.2)',
                    color: 'var(--cyber-orange)',
                    border: '1px solid var(--cyber-orange)',
                    padding: '0.5rem 1rem',
                    fontSize: '0.85rem',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Document Modal */}
      {showAddDocument && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="glass-card" style={{ padding: '2rem', width: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Adicionar Documento KYC</h3>
            <form onSubmit={handleUploadDocument}>
              <div className="form-group">
                <label className="form-label">Título *</label>
                <input
                  type="text"
                  className="form-input"
                  value={documentForm.title}
                  onChange={(e) => setDocumentForm({...documentForm, title: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={documentForm.description}
                  onChange={(e) => setDocumentForm({...documentForm, description: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Tipo de Documento</label>
                <select
                  className="form-input"
                  value={documentForm.document_type}
                  onChange={(e) => setDocumentForm({...documentForm, document_type: e.target.value})}
                >
                  <option value="example">Exemplo</option>
                  <option value="requirement">Requisito</option>
                  <option value="template">Modelo</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={documentForm.is_required}
                    onChange={(e) => setDocumentForm({...documentForm, is_required: e.target.checked})}
                  />
                  Documento Obrigatório
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">Ordem de Exibição</label>
                <input
                  type="number"
                  className="form-input"
                  value={documentForm.order}
                  onChange={(e) => setDocumentForm({...documentForm, order: parseInt(e.target.value)})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Arquivo</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setDocumentFile(e.target.files[0])}
                  style={{ 
                    background: 'rgba(255,255,255,0.1)', 
                    color: 'var(--silver-mist)',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    width: '100%'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  Enviar Documento
                </button>
                <button 
                  type="button" 
                  className="btn-ghost" 
                  onClick={() => setShowAddDocument(false)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Crypto Management for Manager
const CryptoManagement = () => {
  const [cryptoSettings, setCryptoSettings] = useState({
    wallet_address: '',
    wallet_type: 'ethereum',
    network: 'mainnet',
    qr_code_pix: '',
    pix_key: '',
    pix_recipient_name: ''
  });
  const [pixQrFile, setPixQrFile] = useState(null);

  useEffect(() => {
    fetchCryptoSettings();
  }, []);

  const fetchCryptoSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crypto-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const settings = await response.json();
      if (settings.length > 0) {
        setCryptoSettings(settings[0]);
      }
    } catch (error) {
      console.error('Error fetching crypto settings:', error);
    }
  };

  const handleSaveCryptoSettings = async () => {
    try {
      let settingsToSave = { ...cryptoSettings };
      
      // Upload PIX QR Code if provided
      if (pixQrFile) {
        const formData = new FormData();
        formData.append('file', pixQrFile);
        
        const token = localStorage.getItem('token');
        const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          settingsToSave.qr_code_pix = uploadResult.file_path;
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/crypto-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settingsToSave)
      });

      if (response.ok) {
        alert('Configurações crypto salvas com sucesso!');
        setPixQrFile(null);
        fetchCryptoSettings();
      }
    } catch (error) {
      alert('Erro ao salvar configurações crypto');
    }
  };

  return (
    <div>
      <h2 style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>💰 Configuração de Pagamentos Crypto</h2>
      
      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Crypto Wallet Settings */}
        <div className="glass-card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>₿ Configurações da Carteira Crypto</h3>
          
          <div className="form-group">
            <label className="form-label">Tipo de Carteira</label>
            <select
              className="form-input"
              value={cryptoSettings.wallet_type}
              onChange={(e) => setCryptoSettings({...cryptoSettings, wallet_type: e.target.value})}
            >
              <option value="bitcoin">Bitcoin</option>
              <option value="ethereum">Ethereum</option>
              <option value="polygon">Polygon</option>
              <option value="bsc">Binance Smart Chain</option>
              <option value="solana">Solana</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Rede</label>
            <select
              className="form-input"
              value={cryptoSettings.network}
              onChange={(e) => setCryptoSettings({...cryptoSettings, network: e.target.value})}
            >
              <option value="mainnet">Mainnet</option>
              <option value="testnet">Testnet</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Endereço da Carteira *</label>
            <input
              type="text"
              className="form-input"
              placeholder="0x... ou endereço da carteira"
              value={cryptoSettings.wallet_address}
              onChange={(e) => setCryptoSettings({...cryptoSettings, wallet_address: e.target.value})}
              style={{ fontFamily: 'monospace' }}
            />
            <small style={{ opacity: 0.7, fontSize: '0.85rem' }}>
              Este endereço será exibido para os alunos realizarem pagamentos
            </small>
          </div>
        </div>

        {/* PIX Settings */}
        <div className="glass-card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>🇧🇷 Configurações PIX</h3>
          
          <div className="form-group">
            <label className="form-label">Nome do Beneficiário</label>
            <input
              type="text"
              className="form-input"
              placeholder="Nome completo ou razão social"
              value={cryptoSettings.pix_recipient_name}
              onChange={(e) => setCryptoSettings({...cryptoSettings, pix_recipient_name: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Chave PIX</label>
            <input
              type="text"
              className="form-input"
              placeholder="CPF, CNPJ, email, telefone ou chave aleatória"
              value={cryptoSettings.pix_key}
              onChange={(e) => setCryptoSettings({...cryptoSettings, pix_key: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label className="form-label">QR Code PIX (Imagem)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPixQrFile(e.target.files[0])}
              style={{ 
                background: 'rgba(255,255,255,0.1)', 
                color: 'var(--silver-mist)',
                cursor: 'pointer',
                padding: '0.75rem',
                borderRadius: '4px',
                border: '1px solid rgba(255,255,255,0.2)',
                width: '100%'
              }}
            />
            <small style={{ opacity: 0.7, fontSize: '0.85rem' }}>
              Faça upload da imagem do QR Code PIX gerado pelo seu banco
            </small>
          </div>

          {cryptoSettings.qr_code_pix && (
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.9rem', opacity: 0.8, marginBottom: '0.5rem' }}>
                QR Code Atual:
              </label>
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${cryptoSettings.qr_code_pix}`} 
                alt="QR Code PIX atual"
                style={{ 
                  width: '150px', 
                  height: '150px', 
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              />
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="glass-card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>👁️ Preview para Alunos</h3>
          <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
            Assim os alunos verão as informações de pagamento:
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div style={{ background: 'rgba(0, 255, 136, 0.1)', padding: '1rem', borderRadius: '8px' }}>
              <h4 style={{ color: 'var(--neon-green)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                💳 Crypto
              </h4>
              <p style={{ fontSize: '0.8rem', margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {cryptoSettings.wallet_address || 'Endereço não configurado'}
              </p>
            </div>
            
            <div style={{ background: 'rgba(0, 255, 255, 0.1)', padding: '1rem', borderRadius: '8px' }}>
              <h4 style={{ color: 'var(--cyber-blue)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                🇧🇷 PIX
              </h4>
              <p style={{ fontSize: '0.8rem', margin: 0 }}>
                {cryptoSettings.pix_key || 'Chave não configurada'}
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ textAlign: 'center' }}>
          <button 
            className="btn-primary" 
            onClick={handleSaveCryptoSettings}
            style={{ padding: '1rem 2rem' }}
          >
            💾 Salvar Configurações
          </button>
        </div>
      </div>
    </div>
  );
};

// Thumbnail Management Component
const ThumbnailManagement = () => {
  const [contentType, setContentType] = useState('books');
  const [contentItems, setContentItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchContent();
  }, [contentType]);

  const fetchContent = async () => {
    try {
      let endpoint = '';
      switch (contentType) {
        case 'books':
          endpoint = '/api/books';
          break;
        case 'podcasts':
          endpoint = '/api/podcasts';
          break;
        case 'course':
          endpoint = '/api/course';
          break;
        default:
          return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`);
      const data = await response.json();
      
      if (contentType === 'course') {
        setContentItems(data.modules || []);
      } else {
        setContentItems(data || []);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleThumbnailUpload = async () => {
    if (!selectedItem || !thumbnailFile) {
      alert('Selecione um item e uma imagem');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('thumbnail', thumbnailFile);
      formData.append('content_type', contentType);
      formData.append('content_id', selectedItem);

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload/thumbnail`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert('Miniatura definida com sucesso!');
        setThumbnailFile(null);
        setSelectedItem('');
        fetchContent();
      } else {
        throw new Error('Erro ao definir miniatura');
      }
    } catch (error) {
      alert('Erro ao definir miniatura');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card" style={{ padding: '2rem' }}>
      <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '2rem' }}>🖼️ Gerenciar Miniaturas</h3>
      
      <div className="form-group">
        <label className="form-label">Tipo de Conteúdo</label>
        <select
          className="form-input"
          value={contentType}
          onChange={(e) => {
            setContentType(e.target.value);
            setSelectedItem('');
          }}
          style={{ cursor: 'pointer' }}
        >
          <option value="books">📚 Livros</option>
          <option value="podcasts">🎧 Podcasts</option>
          <option value="course">🎓 Módulos do Curso</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Selecionar Item</label>
        <select
          className="form-input"
          value={selectedItem}
          onChange={(e) => setSelectedItem(e.target.value)}
          style={{ cursor: 'pointer' }}
        >
          <option value="">Selecione um item...</option>
          {contentItems.map((item) => (
            <option key={item.id} value={item.id}>
              {contentType === 'course' ? `Módulo ${item.order}: ${item.title}` : item.title}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Upload de Miniatura</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setThumbnailFile(e.target.files[0])}
          style={{ 
            background: 'rgba(255,255,255,0.1)', 
            color: 'var(--silver-mist)',
            cursor: 'pointer',
            padding: '0.75rem',
            borderRadius: '4px',
            border: '1px solid rgba(255,255,255,0.2)',
            width: '100%'
          }}
        />
        <small style={{ opacity: 0.7, fontSize: '0.85rem' }}>
          Formatos suportados: JPG, PNG, WEBP (Recomendado: 300x400px)
        </small>
      </div>

      {selectedItem && contentItems.find(item => item.id === selectedItem) && (
        <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '1rem', background: 'rgba(0,255,136,0.1)' }}>
          <h4 style={{ color: 'var(--neon-green)', marginBottom: '0.5rem' }}>
            Preview do Item Selecionado:
          </h4>
          <p style={{ margin: 0, opacity: 0.9 }}>
            {contentItems.find(item => item.id === selectedItem)?.title}
          </p>
          {contentItems.find(item => item.id === selectedItem)?.thumbnail && (
            <div style={{ marginTop: '1rem' }}>
              <img 
                src={contentItems.find(item => item.id === selectedItem)?.thumbnail} 
                alt="Miniatura atual"
                style={{ 
                  width: '100px', 
                  height: '120px', 
                  objectFit: 'cover', 
                  borderRadius: '4px',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              />
              <p style={{ fontSize: '0.8rem', opacity: 0.7, margin: '0.5rem 0 0 0' }}>
                Miniatura atual
              </p>
            </div>
          )}
        </div>
      )}

      <button 
        className="btn-primary" 
        onClick={handleThumbnailUpload}
        disabled={!selectedItem || !thumbnailFile || loading}
        style={{ width: '100%' }}
      >
        {loading ? '📤 Enviando...' : '🖼️ Definir Miniatura'}
      </button>
    </div>
  );
};

// Book Reader Modal
const BookReaderModal = ({ book, onClose }) => {
  return (
    <div 
      className="modal-overlay" 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="glass-card" 
        style={{
          width: '90%',
          height: '90%',
          maxWidth: '800px',
          padding: '2rem',
          position: 'relative',
          overflow: 'auto'
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            color: 'var(--silver-mist)',
            fontSize: '1.5rem',
            cursor: 'pointer',
            opacity: 0.7
          }}
        >
          ×
        </button>

        <div style={{ paddingRight: '3rem' }}>
          <h2 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>
            📖 {book.title}
          </h2>
          <p style={{ color: 'var(--neon-green)', marginBottom: '2rem' }}>
            {book.subtitle}
          </p>
          
          <div className="glass-card" style={{ padding: '2rem', marginBottom: '2rem' }}>
            <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>Prévia do Conteúdo</h3>
            <p style={{ lineHeight: '1.6', marginBottom: '1rem' }}>
              {book.description}
            </p>
            <p style={{ fontSize: '0.9rem', opacity: 0.8, fontStyle: 'italic' }}>
              Esta é uma prévia limitada do conteúdo. Para acessar o livro completo, 
              adquira-o através do link no Clube de Autores.
            </p>
          </div>

          <div style={{ textAlign: 'center' }}>
            <a 
              href={book.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ 
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              🛒 Comprar Livro Completo
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;